import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React, { useMemo, useState } from 'react';
import { ControllStatus, getMaturityLevelName } from 'src/api/types';
import { GTCheckBox, GTColumnSelectAnchor, GTExpander, GTFrameworkCell, GTTdm } from 'src/components/GenericTable';
import { formatDate, formatNumber } from 'src/utils/misc';
import { filter } from 'lodash';
import { Link } from 'react-router-dom';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { gTCheckHandler } from 'src/components/GenericTable/util';
import { mpEvent } from 'src/utils/mixpanel/useMixPanel';
import { MPEvents } from 'src/utils/mixpanel/types';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { OpenInNew } from '@mui/icons-material';
import { TLLink } from '../DashboardPage/comps';
import { TextOverflowTooltip } from 'src/common';
import { ControlsQuery } from 'src/graphql/_generated';

type Control = ControlsQuery['controls'][number];

type DeleteModalValue = {
  id: string | null;
  hasScenarios?: number;
  isLive?: boolean;
};

type LinkScenarioModalValue = {
  id: string | null;
  name?: string | null;
  strategy?: string | null;
};

type UseColumns = (a: { data?: Control[] }) => {
  columns: ColumnDef<Control>[];
  GTColumnSelectAnchorExported: JSX.Element;
  deleteModal: DeleteModalValue;
  setDeleteModal: (a: DeleteModalValue) => void;
  controlEditId: string | undefined;
  setControlEditId: (id: string | undefined) => void;
  linkScenarioModal: LinkScenarioModalValue;
  setLinkScenarioModal: (data: LinkScenarioModalValue) => void;
  checked: string[];
  resetChecked: () => void;
};

const defaultColumns = ['strategy', 'name', 'owner', 'framework_controls', 'status', 'updated_at'];

export const useColumns: UseColumns = ({ data = [] }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(`${TableIds.rmp}-columns`, defaultColumns);
  const [deleteModal, setDeleteModal] = useState<DeleteModalValue>({ id: null });
  const [linkScenarioModal, setLinkScenarioModal] = useState<LinkScenarioModalValue>({ id: null });
  const [controlEditId, setControlEditId] = useState<string | undefined>(undefined);
  const [checked, setChecked] = useState<string[]>([]);

  const columns = useMemo(() => {
    const internalIds = [GTInternalIds.expander, GTInternalIds.tdm, GTInternalIds.checker];

    const list: ColumnDef<Control>[] = [
      {
        id: GTInternalIds.checker,
        header: ({ table }) => {
          const { isAllChecked, checkAllHandler } = gTCheckHandler(
            {
              checked,
              selector: 'id',
              table,
            },
            setChecked,
          );

          return <GTCheckBox isChecked={isAllChecked} onClick={checkAllHandler} />;
        },
        cell: ({ row }) => {
          const id = row.original.id;
          return (
            <GTCheckBox
              isChecked={checked.includes(id)}
              onClick={() => {
                if (checked.includes(id)) {
                  setChecked(checked.filter((el) => el !== id));
                } else {
                  setChecked([...checked, id]);
                }
              }}
            />
          );
        },
        size: 1,
        maxSize: 35,
        enableSorting: false,
      },
      {
        id: GTInternalIds.expander,
        header: () => null,
        cell: ({ row }) => {
          return row.getCanExpand() ? (
            <GTExpander onClick={row.getToggleExpandedHandler()} isExpanded={row.getIsExpanded()} />
          ) : null;
        },
        size: 1,
        maxSize: 20,
        enableSorting: false,
      },
      {
        id: 'ux_id',
        header: 'Id',
        accessorFn: (row) => row.id || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'strategy',
        header: 'Strategy',
        accessorFn: (row) => row.strategy || null,
        cell: (info) => info.getValue(),
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => row.name,
        cell: (info) => (
          <TextOverflowTooltip>
            <Link
              to={`/risk-management/control/${info.row.original.id}`}
              className="table-link"
              onClick={(e) => {
                e.stopPropagation();
                mpEvent(MPEvents.Link, {
                  source: {
                    value: ['RMP'],
                  },
                  destination: { value: ['Control'], params: { id: info.row.original.id } },
                  tags: ['RMP'],
                });
              }}
            >
              {info.getValue() as string}
            </Link>
          </TextOverflowTooltip>
        ),
        sortingFn: sortingFns.text,
      },
      {
        id: 'owner',
        header: 'Owner',
        accessorFn: (row) => row.owner?.text,
        cell: (info) => info.getValue(),
        sortingFn: sortingFns.text,
      },
      {
        id: 'framework_controls',
        header: 'Framework',
        accessorFn: (row) =>
          row.frameworkControls.map((el) => `${el.controlFramework?.shortname} (${el.control_identifier})`).join(', '),
        cell: (info) => <GTFrameworkCell controls={info.row.original.frameworkControls} />,
        sortingFn: sortingFns.text,
      },
      {
        id: 'annual_cost',
        header: 'Annual Cost',
        accessorFn: (row) => row.annualCost,
        cell: ({ row }) => formatNumber(row.original.annualCost, { symbol: true }),
        sortingFn: sortingFns.alphanumeric,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: (info) => info.getValue(),
      },
      {
        id: 'existingMaturity',
        header: 'Existing Maturity',
        cell: ({ row }) => getMaturityLevelName(row.original.existingMaturity),
      },
      {
        id: 'targetMaturity',
        header: 'Target Maturity',
        cell: ({ row }) => getMaturityLevelName(row.original.targetMaturity),
      },
      {
        id: 'updated_at',
        header: 'Last Edited',
        accessorFn: (row) => row.meta?.updatedAt,
        cell: ({ row }) => {
          const date = row.original.meta?.updatedAt;
          if (!date) return null;
          return (
            <TLLink date={date}>
              {formatDate(date, {
                formatType: 'datetime',
              })}
            </TLLink>
          );
        },
      },
      {
        id: 'source',
        header: 'Link',
        accessorFn: (row) => row.linkUrl,
        cell: (info) => {
          const url = info.getValue();
          const linkText = (info.row.original.linkText || url) as string;

          return url ? (
            <a
              href={url as string}
              target="_blank"
              rel="noopener noreferrer"
              className="table-link"
              onClick={(e) => e.stopPropagation()}
            >
              {linkText}{' '}
              <OpenInNew
                css={`
                  font-size: 14px !important;
                  color: inherit;
                `}
              />
            </a>
          ) : (
            <span>{linkText || '-'}</span>
          );
        },
      },
      {
        id: GTInternalIds.tdm,
        header: () => null,
        cell: ({ row }) => {
          const onEditClick = () => setControlEditId(row.original.id);
          const onLinkScenarioClick = () =>
            setLinkScenarioModal({ id: row.original.id, name: row.original.name, strategy: row.original.strategy });
          const onDeleteClick = () =>
            setDeleteModal({
              id: row.original.id,
              hasScenarios: row.original.riskManagementPlansCount || 0,
              isLive: row.original.status === ControllStatus.Live,
            });

          return (
            <div css="display: flex;grid-gap: 8px">
              <GTTdm
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                onLinkScenarioClick={onLinkScenarioClick}
              />
            </div>
          );
        },
        size: 1,
      },
    ];

    return filter(list, (el) => [...internalIds, ...selectedColumns].includes(el.id as string));
  }, [data, selectedColumns, checked]);

  const GTColumnSelectAnchorExported = (
    <GTColumnSelectAnchor
      tableId={TableIds.rmp}
      options={[
        { key: 'ux_id', title: 'Id' },
        { key: 'strategy', title: 'Strategy' },
        { key: 'name', title: 'Name' },
        { key: 'owner', title: 'Owner' },
        { key: 'framework_controls', title: 'Framework' },
        { key: 'annual_cost', title: 'Annual Cost' },
        { key: 'status', title: 'Status' },
        { key: 'existingMaturity', title: 'Existing Maturity' },
        { key: 'targetMaturity', title: 'Target Maturity' },
        { key: 'updated_at', title: 'Last Edited' },
        { key: 'source', title: 'Link' },
      ]}
      defaultOptions={defaultColumns}
      value={selectedColumns}
      onChange={setSelectedColumns}
    />
  );

  return {
    columns,
    GTColumnSelectAnchorExported,
    deleteModal,
    setDeleteModal,
    controlEditId,
    setControlEditId,
    checked,
    linkScenarioModal,
    setLinkScenarioModal,
    resetChecked: () => setChecked([]),
  };
};
